import React, { useCallback, useMemo, useState } from "react";
import { Icon } from "@fluentui/react/lib/Icon";
import useAppNavigation from "system/hooks/use-app-navigation";
import { useConfiguration, usePageNavigation, useResponsive } from "system/hooks";
import { useLocation } from "react-router-dom";
import { registerIcons } from "@fluentui/style-utilities";
import { AskAidenIcon } from "components/modules/ask-aiden/assets";
import AskAidenAccess from "system/types/enums/AskAidenAccess";
import { Tooltip } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "system/store";
import ConfirmationDialog from "./ConfirmationDialog";
import { manageAlertIcon } from "components/modules/manage-alert/assets";
import { Roles } from "system/types/enums/Roles";
import { selectCustomPages } from "system/store/slices/custom-page-slice";


interface IMenuItem {
  key: string;
  title: string;
  icon: string;
  route?: string;
  url?: string;
  visible: boolean;
  shouldUpdateState?: boolean;
}

// Registering custom icon to Fluent icons
registerIcons({
  icons: {
    AskAiden: <img src={AskAidenIcon} alt="AskAidenIcon" style={{ width: 21, height: 31.5 }} />,
    ManageAlert: <img src={manageAlertIcon} alt="manageAlertIcon" />,
  },
});

export const Sidebar: React.FC = () => {
  const [collapsed, setCollapsed] = useState(true);
  const { isGlobalAdmin, isFileModuleEnabled, isVulnerabilitiesModuleEnabled, isPolicyEditorEnabled, isAskAidenEnabled, isAuditReportEnabled, isWidgetReportEnabled, hasAdminPermission, roles } = useConfiguration();
  const { toggleMobileSidebarVisibility, isMobileSidebarVisible } = useResponsive();
  const { navigateToHomePage } = usePageNavigation();

  const navigate = useAppNavigation();
  const location = useLocation();
  const modifiedRows = useSelector((state: RootState) => state.policyEditor.modifiedRows);

  const [showCancelWarning, setShowCancelWarning] = useState(false);
  const [pendingNavigationRoute, setPendingNavigationRoute] = useState<string | null>(null);

  const hasPolicyEditorAccess = useMemo(
    () => isPolicyEditorEnabled && (roles.includes(Roles.PolicyAdmins) || roles.includes(Roles.PolicyViewers) || isGlobalAdmin),
    [isPolicyEditorEnabled, isGlobalAdmin, roles]
  );
  const pages = useSelector(selectCustomPages)
  const menuItems = useMemo<IMenuItem[]>(() => {
    const allItems: IMenuItem[] = [
      {
        key: "dashboard",
        title: "Dashboard",
        icon: "Home",
        route: "/dashboard",
        visible: true,
        shouldUpdateState: true,
      },
      {
        key: "packages",
        title: "Packages",
        icon: "AllApps",
        route: "/packages",
        visible: isGlobalAdmin,
      },
      {
        key: "policyeditorF",
        title: "Policy Editor",
        icon: "FileTemplate",
        route: "/policyeditor",
        visible: hasPolicyEditorAccess,
      },
      {
        key: "documents",
        title: "Documents",
        icon: "DocLibrary",
        route: "/documents",
        visible: isFileModuleEnabled,
      },
      {
        key: "support",
        title: "Support",
        icon: "QandA",
        url: process.env.REACT_APP_SUPPORT_URL,
        visible: !!process.env.REACT_APP_SUPPORT_URL,
      },
      {
        key: "askAiden",
        title: "AskAiden",
        icon: "AskAiden",
        route: "/askaiden",
        visible: isAskAidenEnabled !== AskAidenAccess.NoAccess,
      },
      {
        key: "askAidenDocuments",
        title: "AskAiden Documents",
        icon: "DocLibrary",
        route: "/askaidentdocuments",
        // Matches the route gate in MainRouter.tsx - without the enabled check, admins on a
        // tenant with Ask Aiden turned off still see this link, and it only redirects them.
        visible: isAskAidenEnabled !== AskAidenAccess.NoAccess && hasAdminPermission,
      },
      {
        key: "Admin",
        title: "Admin",
        icon: "admin",
        route: "/admin",
        visible: hasAdminPermission,
      },
      {
        key: "Manage Alert",
        title: "Manage Alert",
        icon: "ManageAlert",
        route: "/managealert",
        visible: isGlobalAdmin,
      },
      {
        key: "vulnerabilities",
        title: "Vulnerabilities",
        icon: "Warning12",
        route: "/vulnerabilities",
        visible: isVulnerabilitiesModuleEnabled,
      },
      {
        key: "auditreport",
        title: "Audit Reports",
        icon: "Copy",
        route: "/auditreport",
        visible: isAuditReportEnabled,
      },
      {
        key: "advancedreporting",
        title: "Advanced Reporting",
        icon: "ReportDocument",
        route: "/advancedreporting",
        visible: isWidgetReportEnabled,
      },
    ];

    return allItems.filter((x) => x.visible);
  }, [isGlobalAdmin, hasPolicyEditorAccess, isFileModuleEnabled, isAskAidenEnabled, hasAdminPermission, isVulnerabilitiesModuleEnabled, isAuditReportEnabled, isWidgetReportEnabled]);

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const handleItemClick = (item: IMenuItem) => {
    if (
      modifiedRows.length > 0 &&
      item.route &&
      (location.pathname.includes("/policyeditor") || location.pathname === "/policyeditorsubject")
    ) {
      setPendingNavigationRoute(item.route);
      setShowCancelWarning(true);
    } else {
      if (item.key === "dashboard") {
        navigateToHomePage();
      } else if (item.route) {
        if (item.shouldUpdateState) {
          navigate(item.route, { state: { forceRefresh: Date.now() } });
        } else {
          navigate(item.route);
        }
      } else if (item.url) {
        window.open(item.url, "_blank");
      }
    }
  };

  const handleConfirmWarning = () => {
    if (pendingNavigationRoute) {
      if (pendingNavigationRoute === "/dashboard") {
        navigateToHomePage();
      } else {
        navigate(pendingNavigationRoute);
      }
    }

    setShowCancelWarning(false);
    setPendingNavigationRoute(null);
  };

  const handleCancelWarning = () => {
    setShowCancelWarning(false);
    setPendingNavigationRoute(null);
  };

  const getActiveItemIndex = useCallback(() => {
    const isOnDynamicPage = pages?.some((page) =>
      location.pathname.includes(`/page/${page.id}`)
    );
    if (isOnDynamicPage) return -1;

    const activeIndex = menuItems.findIndex((item) => {
      if (!item.route) return false;
      return location.pathname.includes(item.route);
    });
    return activeIndex >= 0 ? activeIndex : 0;
  }, [menuItems, location.pathname, pages]);

  const activeItemIndex = useMemo(() => getActiveItemIndex(), [getActiveItemIndex]);

  const isActive = useCallback(
    (index: number) => activeItemIndex !== -1 && index === activeItemIndex,
    [activeItemIndex],
  );

  return (
    <>
      <div className={`sidebar ${collapsed ? "sidebar--collapsed" : "sidebar--expanded"}`}>
        <Icon
          iconName={collapsed ? "DoubleChevronRight12" : "DoubleChevronLeft12"}
          className="sidebar__toggle-button"
          onClick={toggleCollapsed}
        />
        <ul className="sidebar__items">
          {menuItems.map((item, index) => (
            <li
              key={item.key}
              className={`sidebar__item ${isActive(index) ? "sidebar__item--active" : ""}`}
              onClick={() => handleItemClick(item)}
            >
              {!collapsed ? (
                <span>
                  <Icon iconName={item.icon} className="sidebar__item--icon" />
                </span>
              ) : (
                <Tooltip title={item.title} placement="right">
                  <span>
                    <Icon iconName={item.icon} className="sidebar__item--icon" />
                  </span>
                </Tooltip>
              )}
              {!collapsed && <span className="sidebar__item--title">{item.title}</span>}
            </li>
          ))}
          {/* <DynamicPageNavItems
            collapsed={collapsed}
            activePath={location.pathname}
            onNavigate={(route) => navigate(route)}
            isWidgetReportEnabled={isWidgetReportEnabled}
          /> */}
        </ul>
      </div>

      {/* Mobile sidebar */}
      <div className={`mobile-sidebar ${isMobileSidebarVisible ? "mobile-sidebar--visible" : ""}`}>
        <ul className="mobile-sidebar__items">
          {menuItems &&
            menuItems.map((item, index) => (
              <li
                key={item.key}
                className={`mobile-sidebar__item ${isActive(index) ? "mobile-sidebar__item--active" : ""}`}
                onClick={() => {
                  handleItemClick(item);
                  toggleMobileSidebarVisibility();
                }}
              >
                <Icon iconName={item.icon} className="mobile-sidebar__item--icon" />
                <span className="mobile-sidebar__item--title">{item.title}</span>
              </li>
            ))}
          {/* <DynamicPageNavItems
            collapsed={false}
            activePath={location.pathname}
            onNavigate={(route) => {
              navigate(route);
              toggleMobileSidebarVisibility();
            }}
            isWidgetReportEnabled={isWidgetReportEnabled}
          /> */}
        </ul>
      </div>

      <ConfirmationDialog
        title="Unsaved Changes"
        subText="You have pending changes that will be lost if you proceed. Are you sure you want to continue?"
        isVisible={showCancelWarning}
        onConfirm={handleConfirmWarning}
        onCancel={handleCancelWarning}
      />
    </>
  );
};